import { Stack, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import SettingsPageLayout from 'domains/core/components/SettingsPageLayout';
import useBrandProfile from 'hooks/queries/useBrandProfile';
import { URLPaths } from 'models/enums';

import BrandSetupEditPage from './BrandSetupEditPage';
import BrandSetupEmptyStatePage from './BrandSetupEmptyStatePage';
import BrandSetupErrorPage from './BrandSetupErrorPage';
import BrandSetupLandingPage from './BrandSetupLandingPage';

/**
 * Renders Brand Setup empty/landing when on /settings/brand;
 * renders edit form when on /settings/brand/edit.
 */
const SettingsBrandRoute = () => {
    const location = useLocation();
    const isEditPath = location.pathname === URLPaths.SETTINGS_BRAND_EDIT;

    if (isEditPath) {
        return <BrandSetupEditPage />;
    }
    return <BrandSetupRouter />;
};

/**
 * Renders empty state or landing based on brand profile data.
 */
const BrandSetupRouter = () => {
    const { data: brandProfile, isLoading, isError, refetch } = useBrandProfile();

    if (isLoading) {
        return (
            <Stack data-testid="brand-setup-loading" role="main">
                <SettingsPageLayout header="brand setup" headerIcon={<SettingsSectionHeaderIcon />}>
                    <Stack p={4} alignItems="center">
                        <Typography variant="body1">Loading...</Typography>
                    </Stack>
                </SettingsPageLayout>
            </Stack>
        );
    }
    if (isError) {
        return <BrandSetupErrorPage onRetry={refetch} />;
    }

    const hasAnyValues = brandProfile?.some((field) => {
        const v = field.currentValue;
        if (v === null || v === undefined) return false;
        if (v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    });

    if (!hasAnyValues) {
        return <BrandSetupEmptyStatePage />;
    }

    return <BrandSetupLandingPage brandProfile={brandProfile ?? []} />;
};

export default SettingsBrandRoute;
